import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PiPottedPlantLight } from "react-icons/pi";
import { useLocation, useParams } from "react-router-dom";

import { IconChevronDown } from "@/components/_icons/IconChevronDown";
import { IconChevronUp } from "@/components/_icons/IconChevronUp";
import { ENTITY_ROUTE_DESCRIPTOR_LIST } from "@/domain/_shared/entity-permission/entity-route-descriptor";
import { useEffectiveEntityRight } from "@/domain/_shared/entity-permission/hooks/api/use-effective-entity-right";
import { useFeatureFlagSimple } from "@/domain/_shared/feature-flag/hooks/api/use-feature-flag-simple";
import { ClimateActionSuggestionDevSection } from "@/domain/climate-action/components/climate-action-suggestion-dev-section";
import { useGetTeam } from "@/domain/team/hooks/api/use-get-team";
import { useTeamMembershipList } from "@/domain/team/hooks/api/use-team-membership-list";
import { useCurrentUserContext } from "@/domain/user/hooks/use-current-user-context";
import { useWorkspaceGetByUrl } from "@/domain/workspace/hooks/api/use-workspace-by-url";

interface EntityContext {
  entityId: string;
  entityType: string;
  labelKey: (typeof ENTITY_ROUTE_DESCRIPTOR_LIST)[number]["labelKey"];
}

function useEntityContextFromPath(pathname: string): EntityContext | null {
  return useMemo(() => {
    const pathParts = pathname.split("/");
    for (const mapping of ENTITY_ROUTE_DESCRIPTOR_LIST) {
      const segmentIndex = pathParts.findIndex((part) => part === mapping.segment);
      if (segmentIndex >= 0 && segmentIndex < pathParts.length - 1) {
        const entityId = pathParts[segmentIndex + 1];
        if (entityId && entityId !== "all" && entityId !== "dashboard" && entityId !== "upload") {
          return { entityId, entityType: mapping.entityType, labelKey: mapping.labelKey };
        }
      }
    }
    return null;
  }, [pathname]);
}

const RIGHT_COLORS: Record<string, string> = {
  OWNER: "#22c55e",
  EDITOR: "#3b82f6",
  VIEWER: "#f59e0b",
  NONE: "#ef4444",
};

// This is just a simple component to not have to always look at the DB
// It will show in dev mode by default.
// In not dev mode or if you close it you can open it again by calling console.log("lumos") in the console
// It's not super reliable in some places of the app and could be a provider instead to keep track of current team for example
export function DebugInfo() {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { t } = useTranslation("user");
  const { currentUser, isSuperUser } = useCurrentUserContext();

  // Extract workspace and team information from URL
  const { workspaceUrl } = useParams();
  const location = useLocation();

  // Extract workspaceId from URL path if it's not in params. Reserved
  // top-level segments (e.g. /admin) are not workspaces — resolving them
  // against workspaceByUrl would 404 on the backend.
  const workspaceIdFromPath = useMemo(() => {
    const pathParts = location.pathname.split("/");
    const WORKSPACE_INDEX = 1;
    const segment = pathParts[WORKSPACE_INDEX];
    return segment === "admin" ? undefined : segment;
  }, [location.pathname]);

  const effectiveWorkspaceUrl = workspaceUrl || workspaceIdFromPath;

  // Extract teamId from URL path if it's not in params
  const teamIdFromPath = useMemo(() => {
    const pathParts = location.pathname.split("/");
    const teamIndex = pathParts.findIndex((part) => part === "teams");
    return teamIndex >= 0 && teamIndex < pathParts.length - 1 ? pathParts[teamIndex + 1] : undefined;
  }, [location.pathname]);

  const teamId = useParams().teamId || teamIdFromPath;

  // Setup console command to show debug panel
  useEffect(() => {
    // Check if debug mode is stored in localStorage
    const storedDebugMode = localStorage.getItem("ark_debug_mode") === "true";
    setIsVisible(process.env.NODE_ENV === "development" || storedDebugMode);

    // Define the console command to show debug panel
    const originalConsoleLog = console.log;
    console.log = function (...args) {
      if (args.length === 1 && args[0] === "lumos") {
        setIsVisible(true);
        localStorage.setItem("ark_debug_mode", "true");
        originalConsoleLog("✨ Debug panel activated! ✨");
        return;
      }
      originalConsoleLog.apply(console, args);
    };

    // Cleanup function to restore original console.log
    return () => {
      console.log = originalConsoleLog;
    };
  }, []);

  // Function to hide the debug panel
  const hideDebugPanel = () => {
    setIsVisible(false);
    localStorage.removeItem("ark_debug_mode");
  };

  // Get workspace data using the URL
  const { workspace } = useWorkspaceGetByUrl({ workspaceUrl: effectiveWorkspaceUrl ?? "" });
  const { team } = useGetTeam({ id: teamId ?? undefined });
  const { teamMembershipList } = useTeamMembershipList({
    teamId: teamId ?? "",
  });

  const { isEnabled: isV2Enabled } = useFeatureFlagSimple({ id: "enable-permission-v2" });

  const entityContext = useEntityContextFromPath(location.pathname);

  const isOnSuggestionPage = useMemo(
    () => /^\/[^/]+\/climate-actions\/standard\/?$/.test(location.pathname),
    [location.pathname],
  );

  const { effectiveRight, loading: v2Loading } = useEffectiveEntityRight({
    entityId: entityContext?.entityId ?? "",
    entityType: entityContext?.entityType ?? "",
    workspaceId: workspace?.id ?? "",
    enabled: isV2Enabled && !!entityContext && !!workspace?.id,
  });

  const isWorkspaceAdmin = useMemo(() => {
    if (!workspace) {
      return false;
    }

    const currentUserWorkspaceMembership = currentUser?.workspaceMembershipList?.find(
      (membership) => membership.workspaceId === workspace.id,
    );

    return currentUserWorkspaceMembership?.role === "ADMIN";
  }, [currentUser, workspace]);

  const isTeamAdmin = useMemo(() => {
    if (!team || !currentUser) {
      return false;
    }

    const currentUserTeamMembership = teamMembershipList?.find((membership) => membership.userId === currentUser.id);

    return currentUserTeamMembership?.role === "ADMIN";
  }, [currentUser, team, teamMembershipList]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed bottom-3 right-3 z-9999 flex max-h-[95vh] flex-col rounded-md border border-border bg-background p-2 text-xs text-foreground ${
        isExpanded && isOnSuggestionPage && process.env.NODE_ENV === "development" ? "w-[360px]" : ""
      }`}
    >
      <div
        className="shrink-0"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <strong>
          <PiPottedPlantLight />
        </strong>
        <div style={{ display: "flex", gap: "4px" }}>
          <button
            aria-label={isExpanded ? "Collapse debug info" : "Expand debug info"}
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: "2px 6px",
              fontSize: "12px",
            }}
          >
            {isExpanded ? <IconChevronUp className="h-2 w-2" /> : <IconChevronDown className="h-2 w-2" />}
          </button>
          <button
            onClick={hideDebugPanel}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: "2px 6px",
              fontSize: "12px",
              color: "#999",
            }}
            title="Close debug panel"
            aria-label="Close debug panel"
          >
            ✕
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="min-h-0 flex-1 overflow-y-auto" style={{ marginTop: "4px" }}>
          <div style={{ marginBottom: "4px", fontWeight: 600, opacity: 0.5 }}>V1 (ABAC)</div>
          Super Admin: {isSuperUser ? "Yes" : "No"}
          <br />
          Workspace Admin: {isWorkspaceAdmin ? "Yes" : "No"}
          <br />
          Team Admin: {isTeamAdmin ? "Yes" : "No"}
          <br />
          Team ID: {teamId || "None"}
          <br />
          Workspace URL: {effectiveWorkspaceUrl || "None"}

          {isV2Enabled && (
            <>
              <div style={{ margin: "6px 0 4px", borderTop: "1px solid", borderColor: "inherit", opacity: 0.2 }} />
              <div style={{ marginBottom: "4px", fontWeight: 600, opacity: 0.5 }}>V2 (Entity)</div>
              {entityContext ? (
                <>
                  Entity: {t(entityContext.labelKey)}
                  <br />
                  ID: <span style={{ fontFamily: "monospace", fontSize: "10px" }}>{entityContext.entityId}</span>
                  <br />
                  Right:{" "}
                  {v2Loading ? (
                    "..."
                  ) : (
                    <span
                      style={{
                        fontWeight: 600,
                        color: RIGHT_COLORS[effectiveRight ?? "NONE"] ?? RIGHT_COLORS.NONE,
                      }}
                    >
                      {effectiveRight ?? "NONE"}
                    </span>
                  )}
                </>
              ) : (
                <span style={{ opacity: 0.5 }}>No entity in context</span>
              )}
            </>
          )}

          {process.env.NODE_ENV === "development" && isOnSuggestionPage && workspace?.id && (
            <>
              <div style={{ margin: "6px 0 4px", borderTop: "1px solid", borderColor: "inherit", opacity: 0.2 }} />
              <ClimateActionSuggestionDevSection workspaceId={workspace.id} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
